import { NgFor, NgIf } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';

interface Option {
  label: string;
  value: any;
}

@Component({
  selector: 'app-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [NgIf, NgFor, FormsModule],
})
export class MultiSelectComponent implements ControlValueAccessor, Validator {
  @Input() label: string = '';
  @Input() placeholder: string = 'Select options';
  @Input() options: Option[] = [];
  @Input() required: boolean = false;
  @Input() errorMessage: string = 'This field is required.';
  @Input() searchable: boolean = true;
  @Input() searchPlaceholder: string = 'Type to search...';
  @Input() noOptionsText: string = 'No options found';
  @Input() caseSensitive: boolean = false;
  @Input() hideSelectedTags: boolean = false;
  @Input() inlineDropdown: boolean = false;
  @Input() showCheckBox: boolean = false;
  @Output() selectionChange = new EventEmitter<any[]>();

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  selectedOptions: Option[] = [];
  selectedOptionValues: any[] = [];
  filteredOptions: Option[] = [];
  originalFilteredOptions: Option[] = [];
  isOpen: boolean = false;
  disabled: boolean = false;
  searchTerm: string = '';
  touched: boolean = false;
  submitted: boolean = false;

  private onChange: (value: any[]) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  ngOnInit() {
    this.originalFilteredOptions = [...this.options];
    this.filteredOptions = [...this.options];
  }

  ngOnChanges() {
    this.originalFilteredOptions = [...this.options];
    this.filterOptions(this.searchTerm);
    this.onValidatorChange();
  }

  get showError(): boolean {
    return (
      this.required &&
      this.selectedOptionValues.length === 0 &&
      (this.touched || this.submitted)
    );
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  constructor(private elRef: ElementRef) {}

  // Validator interface implementation
  validate(control: AbstractControl): ValidationErrors | null {
    if (this.required) {
      const value = control.value;
      const isValid = value && Array.isArray(value) && value.length > 0;
      return isValid ? null : { required: true };
    }
    return null;
  }

  // Add this method to your component class
  highlightSearchTerm(text: string): string {
    if (!this.searchTerm || !text) return text;

    const searchTerm = this.caseSensitive
      ? this.searchTerm
      : this.searchTerm.toLowerCase();
    const textForSearch = this.caseSensitive ? text : text.toLowerCase();
    const index = textForSearch.indexOf(searchTerm);

    if (index === -1) return text;

    const before = text.substring(0, index);
    const match = text.substring(index, index + this.searchTerm.length);
    const after = text.substring(index + this.searchTerm.length);

    return `${before}<span class="search-highlight">${match}</span>${after}`;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  toggleDropdown(): void {
    if (!this.disabled) {
      this.isOpen = !this.isOpen;
      this.markAsTouched();

      if (this.isOpen) {
        this.resetSearch();
        if (this.searchable) {
          setTimeout(() => {
            this.searchInput?.nativeElement?.focus();
            this.searchInput.nativeElement.readOnly = false;
          }, 0);
        }
      } else {
        this.resetSearch();
      }
    }
  }

  handleInputClick(event: Event): void {
    event.stopPropagation();
    if (!this.isOpen) {
      this.toggleDropdown();
    }
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.resetSearch();
  }

  private resetSearch(): void {
    this.searchTerm = '';
    this.filteredOptions = [...this.originalFilteredOptions];
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.readOnly = !this.searchable;
      this.searchInput.nativeElement.value = '';
    }
  }

  isOptionDisabled(option: Option): boolean {
    const ALL_VALUE = 'All';
    if (option.value === ALL_VALUE) return false;
    return this.selectedOptionValues.includes(ALL_VALUE);
  }

  toggleOption(option: Option): void {
    const ALL_VALUE = 'All';
    const isSelectingAll = option.value === ALL_VALUE;

    if (isSelectingAll) {
      this.selectedOptionValues = [ALL_VALUE];
      this.selectedOptions = [option];
    } else {
      const allIndex = this.selectedOptionValues.indexOf(ALL_VALUE);
      if (allIndex > -1) {
        this.selectedOptionValues.splice(allIndex, 1);
        this.selectedOptions.splice(allIndex, 1);
      }

      const index = this.selectedOptionValues.indexOf(option.value);
      if (index > -1) {
        this.selectedOptionValues.splice(index, 1);
        this.selectedOptions.splice(index, 1);
      } else {
        this.selectedOptionValues.push(option.value);
        this.selectedOptions.push(option);
      }
    }

    this.onChange(this.selectedOptionValues);
    this.selectionChange.emit(this.selectedOptionValues);
    this.markAsTouched();
    this.onValidatorChange();

    this.searchTerm = '';
    this.filteredOptions = [...this.options];
    this.filterOptions(this.searchTerm);

    if (this.isOpen && this.searchable) {
      setTimeout(() => {
        this.searchInput?.nativeElement?.focus();
      }, 0);
    }
  }

  removeOption(optionValue: any, event: Event): void {
    event.stopPropagation();
    const index = this.selectedOptionValues.indexOf(optionValue);

    if (index > -1) {
      this.selectedOptionValues.splice(index, 1);
      this.selectedOptions.splice(index, 1);
      this.onChange(this.selectedOptionValues);
      this.selectionChange.emit(this.selectedOptionValues);
      this.markAsTouched();
      this.onValidatorChange();

      // Re-filter options to maintain priority
      this.filterOptions(this.searchTerm);
    }
  }

  isSelected(option: Option): boolean {
    return this.selectedOptionValues.includes(option.value);
  }

  onSearch(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;
    this.filterOptions(this.searchTerm);
  }

  private filterOptions(searchTerm: string): void {
    const selectedValuesSet = new Set(this.selectedOptionValues);

    if (!searchTerm) {
      // No search: keep original order but push selected items to the bottom
      this.filteredOptions = [...this.originalFilteredOptions];
      return;
    }

    const searchLower = this.caseSensitive
      ? searchTerm
      : searchTerm.toLowerCase();

    const filtered = this.originalFilteredOptions.filter((option) => {
      const label = this.caseSensitive
        ? option.label
        : option.label.toLowerCase();
      return label.includes(searchLower);
    });

    this.filteredOptions = filtered.sort((a, b) => {
      const aLabel = this.caseSensitive ? a.label : a.label.toLowerCase();
      const bLabel = this.caseSensitive ? b.label : b.label.toLowerCase();

      const aExact = aLabel === searchLower;
      const bExact = bLabel === searchLower;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;

      const aStartsWith = aLabel.startsWith(searchLower);
      const bStartsWith = bLabel.startsWith(searchLower);
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;

      return (
        this.originalFilteredOptions.indexOf(a) -
        this.originalFilteredOptions.indexOf(b)
      );
    });
  }

  writeValue(value: any[]): void {
    if (value && Array.isArray(value)) {
      this.selectedOptionValues = value.map((item) => {
        if (typeof item === 'object' && item !== null) {
          return item.mccPartnerId || item.value || item;
        }
        return item;
      });

      this.selectedOptions = this.selectedOptionValues.map((val) => {
        const option = this.options.find((opt) => opt.value === val);
        if (option) {
          return option;
        }
        return {
          label: this.getLabelFromValue(val),
          value: val,
        };
      });
    } else {
      this.selectedOptionValues = [];
      this.selectedOptions = [];
    }

    this.filterOptions(this.searchTerm);
    this.onValidatorChange();
  }

  private getLabelFromValue(value: any): string {
    if (typeof value === 'object' && value !== null) {
      return (
        value.mccEventPartnerName || value.label || value.name || String(value)
      );
    }
    const option = this.options.find((opt) => opt.value === value);
    return option ? option.label : String(value);
  }

  getOptionLabel(option: any): string {
    if (!option) return '';
    if (typeof option === 'string') {
      return option;
    }

    if (option.label) {
      return option.label;
    }
    if (option.value) {
      const foundOption = this.options.find(
        (opt) => opt.value === option.value,
      );
      return foundOption ? foundOption.label : String(option.value);
    }

    return String(option);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = () => {
      this.touched = true;
      fn();
    };
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.touched = true;
      this.onTouched();
      this.onValidatorChange();
    }
  }

  markAsSubmitted(): void {
    this.submitted = true;
    this.onValidatorChange();
  }

  isValid(): boolean {
    if (this.required) {
      return this.selectedOptionValues.length > 0;
    }
    return true;
  }
}
